/**
 * ReactiveComponent
 * A base class to handle reactive state and DOM rendering for components.
 *
 * Usage:
 * const component = new ReactiveComponent(selector, initialState);
 *
 * 1. Bind state to a DOM element:
 *    component.bindState('key', (value) => {
 *       $('#element').text(value);
 *    });
 *
 * 2. Update state:
 *    component.state.key = 'newValue';
 *
 * 3. Customize render logic for specific state:
 *    component.bindState('key', customRenderFunction);
 *
 * 4. Watch for state changes:
 *    component.watch('key', (newValue, oldValue) => {
 *       console.log('State changed:', newValue, oldValue);
 *    });
 *
 * 5. Computed properties:
 *    component.computed('fullName', ['firstName', 'lastName'], (firstName, lastName) => {
 *       return `${firstName} ${lastName}`;
 *    });
 *
 * Example:
 *
 * const reactiveComponent = new ReactiveComponent('#app', { title: 'Hello World', count: 0 });
 *
 * // Automatically update the DOM when 'title' changes
 * reactiveComponent.bindState('title', (newTitle) => {
 *     $('#title').text(newTitle);
 * });
 *
 * // Automatically update a counter element
 * reactiveComponent.bindState('count', (newCount) => {
 *     $('#counter').text(`Count: ${newCount}`);
 * });
 *
 * // Watch for state changes
 * reactiveComponent.watch('count', (newCount, oldCount) => {
 *     console.log(`Count changed from ${oldCount} to ${newCount}`);
 * });
 *
 * // Update state
 * reactiveComponent.state.title = 'New Title';
 * reactiveComponent.state.count += 1;
 */
export class ReactiveComponent {
    constructor(selector, initialState) {
        this.$el = $(selector);
        this.renderMap = {}; // Store mapping of state and render function
        this.watchers = {}; // Store watchers for state changes
        this.computedMap = {}; // Store computed properties
        this.oldState = {}; // Store previous state values
        this.state = this.reactive(initialState);
    }

    // reactive func: create Proxy to observe state changes
    reactive(state) {
        return new Proxy(state, {
            set: (target, key, value) => {
                const oldValue = target[key];
                target[key] = value;
                this.update(key, value, oldValue); // call update when state changed
                return true;
            },
            get: (target, key) => {
                // Check if it's a computed property
                if (this.computedMap[key]) {
                    return this.computedMap[key].getter();
                }
                return target[key];
            }
        });
    }

    // Bind state with an element or customize render function
    bindState(key, renderFn = null, inputFn = null) {
        const $bindingEl = this.$el.find(`[data-bind="${key}"]`);

        const isInput = $bindingEl.is('input, select, textarea')

        this.renderMap[key] = renderFn || ((value) => {
            if (!isInput) {
                $bindingEl.text(value);
            } else {
                if ($bindingEl.is('input[type="radio"], input[type="checkbox"]')) {
                    $bindingEl.each(function () {
                        const $item = $(this);

                        if ($item.is('input[type="checkbox"]')) {
                            $item.prop('checked', Boolean(value));
                        } else {
                            $item.prop('checked', $item.val() == value);
                        }
                    });
                } else {
                    $bindingEl.val(value);
                }
            }
        });

        // two-way binding for inputs
        if (isInput) {
            const isTextInput = $bindingEl.is('input[type="text"], input[type="number"], input[type="email"], input[type="password"], textarea')
            const eventName = isTextInput ? 'input' : 'change';

            $bindingEl.off(eventName).on(eventName, inputFn || ((event) => {
                this.state[key] = $(event.target).val();
            }));
        }

        // Call render func for first time when render
        this.update(key, this.state[key]);
    }

    // Watch for state changes
    watch(key, callback) {
        if (!this.watchers[key]) {
            this.watchers[key] = [];
        }
        this.watchers[key].push(callback);
    }

    // Create computed properties
    computed(name, dependencies, getter) {
        this.computedMap[name] = {
            dependencies,
            getter: () => {
                const values = dependencies.map(dep => this.state[dep]);
                return getter(...values);
            }
        };
    }

    // Update when state changed
    update(key, value, oldValue) {
        // Call render function
        if (this.renderMap[key]) {
            this.renderMap[key](value);
        }

        // Call watchers
        if (this.watchers[key]) {
            this.watchers[key].forEach(callback => {
                callback(value, oldValue);
            });
        }

        // Update computed properties that depend on this key
        Object.keys(this.computedMap).forEach(computedKey => {
            const computed = this.computedMap[computedKey];
            if (computed.dependencies.includes(key)) {
                // Trigger re-render for computed properties
                this.update(computedKey, this.state[computedKey]);
            }
        });
    }

    // Set multiple state properties at once
    setState(newState) {
        Object.keys(newState).forEach(key => {
            this.state[key] = newState[key];
        });
    }

    // Get current state
    getState() {
        return { ...this.state };
    }

    // Destroy component and clean up
    destroy() {
        // Remove all event listeners
        this.$el.off();
        
        // Clear all watchers and render maps
        this.watchers = {};
        this.renderMap = {};
        this.computedMap = {};
        
        // Remove element from DOM
        this.$el.remove();
    }
}
